import React, { useEffect, useRef } from 'react';
import FormInput from '../atoms/FormInput';
import DatePicker from '../atoms/DatePicker';

/**
 * PassengerForm - Formulario de datos de un pasajero individual
 *
 * Props:
 * - index: Índice del pasajero (0, 1, 2...)
 * - passenger: Objeto con datos del pasajero {
 *     lastName, firstName, birthDate, idType, idNumber, nationality
 *   }
 * - onChange: Función callback (index, fieldName, value) => void
 * - errors: Objeto con errores específicos del pasajero
 * - className: Clases CSS adicionales (opcional)
 */
function PassengerForm({
  index,
  passenger = {
    lastName: '',
    firstName: '',
    birthDate: '',
    idType: 'cedula',
    idNumber: '',
    nationality: ''
  },
  onChange,
  errors = {},
  className = ''
}) {
  // Ref para rastrear si ya se inicializó el idType
  const initialized = useRef(false);

  // Normalizar datos del pasajero - usar ?? para valores null/undefined
  const passengerData = {
    lastName: passenger.lastName ?? '',
    firstName: passenger.firstName ?? '',
    birthDate: passenger.birthDate ?? '',
    idType: passenger.idType ?? 'cedula',
    idNumber: passenger.idNumber ?? '',
    nationality: passenger.nationality ?? ''
  };

  // Sincronizar el valor por defecto SOLO una vez al montar si está undefined
  useEffect(() => {
    if (!initialized.current && passenger.idType === undefined) {
      initialized.current = true;
      onChange(index, 'idType', 'cedula');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar al montar

  const handleChange = (fieldName) => (e) => {
    onChange(index, fieldName, e.target.value);
  };

  const idTypes = [
    { value: 'cedula', label: 'Cédula' },
    { value: 'pasaporte', label: 'Pasaporte' },
    { value: 'extranjeria', label: 'Tarjeta de Extranjería' }
  ];

  return (
    <div className={className}>
      <div className="py-3">
        {/* Last Name */}
        <FormInput
          label="Apellidos"
          required
          placeholder="Ingrese los apellidos"
          value={passengerData.lastName}
          onChange={handleChange('lastName')}
          error={errors.lastName}
        />

        {/* First Name */}
        <FormInput
          label="Nombres"
          required
          placeholder="Ingrese los nombres"
          value={passengerData.firstName}
          onChange={handleChange('firstName')}
          error={errors.firstName}
        />

        {/* Birth Date */}
        <DatePicker
          label="Fecha de Nacimiento"
          required
          placeholder="DD/MM/YYYY"
          value={passengerData.birthDate}
          onChange={handleChange('birthDate')}
          error={errors.birthDate}
          max="2019-12-31"
        />

        {/* ID Type */}
        <div className="mb-3">
          <label className="form-label">
            Tipo de Identificación<span className="text-danger">*</span>
          </label>
          <select
            value={passengerData.idType}
            onChange={handleChange('idType')}
            className={`form-select ${errors.idType ? 'is-invalid' : ''}`}
          >
            {idTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {errors.idType && (
            <div className="invalid-feedback d-block">
              {errors.idType}
            </div>
          )}
        </div>

        {/* ID Number */}
        <FormInput
          label="Número de Identificación"
          required
          placeholder="Ingrese el número"
          value={passengerData.idNumber}
          onChange={handleChange('idNumber')}
          error={errors.idNumber}
        />

        {/* Nationality */}
        <FormInput
          label="Nacionalidad"
          required
          placeholder="Ej: Colombiana"
          value={passengerData.nationality}
          onChange={handleChange('nationality')}
          error={errors.nationality}
        />
      </div>
    </div>
  );
}

export default PassengerForm;
